import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button } from '../ui/Button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/Dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { 
  Users, 
  Truck, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  UserCheck,
  MapPin,
  Clock,
  Shield,
  Info,
  Plus,
  Trash2,
  Search,
  ChevronDown,
  Check,
  X
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { apiService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';

// Clean, normal dropdown that opens directly below the select box,
// perfectly aligned with the field, with built-in search and duplicate protection.
const SearchableAssignmentSelect = ({
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No options found',
  error = false,
  icon: Icon
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  const selectedOption = useMemo(() => {
    return options.find(opt => opt.id === value);
  }, [options, value]);

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const q = searchQuery.toLowerCase().trim();
    return options.filter(opt => 
      opt.primaryText?.toLowerCase().includes(q) ||
      opt.secondaryText?.toLowerCase().includes(q) ||
      opt.extraText?.toLowerCase().includes(q)
    );
  }, [options, searchQuery]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const timer = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 40);

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        type="button"
        onClick={() => {
          if (!isOpen) {
            setSearchQuery('');
          }
          setIsOpen(!isOpen);
        }}
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-md border bg-white px-3 py-2 text-xs sm:text-sm ring-offset-background transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 text-left",
          isOpen ? "border-blue-500 ring-2 ring-blue-500/20" : "border-gray-300 hover:border-gray-400",
          error && "border-red-400 focus:ring-red-200"
        )}
      >
        <div className="flex items-center gap-2 min-w-0 pr-1 overflow-hidden flex-1">
          {Icon && <Icon className="w-4 h-4 text-gray-400 shrink-0" />}
          {selectedOption ? (
            <div className="flex items-center justify-between w-full min-w-0 gap-2">
              <span className="font-medium text-gray-900 truncate">
                {selectedOption.primaryText}
              </span>
              {selectedOption.secondaryText && (
                <span className="text-xs text-gray-500 font-mono bg-gray-100 px-1.5 py-0.5 rounded shrink-0">
                  {selectedOption.secondaryText}
                </span>
              )}
            </div>
          ) : (
            <span className="text-gray-400 text-xs sm:text-sm">{placeholder}</span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0 ml-2">
          {selectedOption && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onChange('');
                setIsOpen(false);
              }}
              className="p-0.5 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600 transition-colors"
              title="Clear selection"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown className={cn("h-4 w-4 text-gray-400 transition-transform duration-150", isOpen && "rotate-180")} />
        </div>
      </button>

      {isOpen && (
        <div className="absolute z-50 left-0 right-0 top-full mt-1.5 w-full flex flex-col bg-white rounded-md border border-gray-200 shadow-xl overflow-hidden animate-in fade-in-0 duration-100">
          {/* Sticky Search Header */}
          <div className="p-2 border-b border-gray-100 bg-gray-50/90 sticky top-0 z-10 shrink-0">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-8 pr-7 py-1.5 text-xs bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-400"
                onClick={(e) => e.stopPropagation()}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="flex items-center justify-between text-[11px] text-gray-400 mt-1 px-1">
              <span>{filteredOptions.length} available</span>
            </div>
          </div>

          {/* Options List */}
          <div className="overflow-y-auto max-h-56 p-1 divide-y divide-gray-50">
            {filteredOptions.length === 0 ? (
              <div className="py-6 text-center text-xs text-gray-500">
                {emptyMessage}
              </div>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = opt.id === value;
                return (
                  <div
                    key={opt.id}
                    onClick={() => {
                      if (opt.disabled) return;
                      onChange(opt.id);
                      setIsOpen(false);
                      setSearchQuery('');
                    }}
                    className={cn(
                      "px-3 py-2 text-xs flex items-center justify-between rounded-md transition-colors",
                      opt.disabled
                        ? "opacity-45 bg-gray-50/70 cursor-not-allowed text-gray-400"
                        : isSelected
                          ? "bg-blue-50 text-blue-900 font-medium cursor-pointer"
                          : "hover:bg-gray-100 text-gray-700 cursor-pointer"
                    )}
                  >
                    <div className="flex flex-col min-w-0 pr-2">
                      <span className={cn("truncate", isSelected ? "font-semibold text-blue-900" : "font-medium text-gray-800")}>
                        {opt.primaryText}
                      </span>
                      {opt.extraText && (
                        <span className="text-[11px] text-gray-400 truncate">{opt.extraText}</span>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0 ml-2">
                      {opt.secondaryText && (
                        <span className="font-mono text-[11px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                          {opt.secondaryText}
                        </span>
                      )}
                      {opt.disabled && opt.disabledReason && (
                        <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded font-medium">
                          {opt.disabledReason}
                        </span>
                      )}
                      {isSelected && (
                        <Check className="w-4 h-4 text-blue-600 ml-1 shrink-0" />
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const DriverAmbulanceAssignmentModal = ({ onSuccess, onError, children, inline = false, onCancel }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [assignmentResult, setAssignmentResult] = useState(null);
  const { user } = useAuth();
  
  const [assignments, setAssignments] = useState([]);
  const [unassignedDrivers, setUnassignedDrivers] = useState([]);
  const [unassignedAmbulances, setUnassignedAmbulances] = useState([]);
  const [errors, setErrors] = useState({});

  // Load data when modal opens
  useEffect(() => {
    if (isOpen) {
      loadAssignmentData();
    }
  }, [isOpen]);

  const loadAssignmentData = async () => {
    setLoadingData(true);
    try {
      // Get operator ID
      const operatorId = user?.operator?.operator_id || 
                        user?.operator_id || 
                        user?.operator?.id ||
                        user?.fleet_operator_id ||
                        user?.fleet_operator?.operator_id;

      console.log('🔄 Loading assignment data for operator:', operatorId);

      // Fetch unassigned drivers and ambulances
      const [driversResponse, ambulancesResponse] = await Promise.all([
        apiService.getMyDrivers({
          limit: 100,
          page: 1,
          availability_status: 'AVAILABLE',
          unassigned_only: true
        }),
        apiService.getAmbulances({ 
          limit: 100, 
          page: 1, 
          operator_id: operatorId,
          status: 'AVAILABLE'
        })
      ]);

      console.log('📋 Drivers response:', driversResponse);
      console.log('🚑 Ambulances response:', ambulancesResponse);

      // Extract drivers and ambulances from response
      let drivers = [];
      let ambulances = [];

      if (driversResponse.success && driversResponse.data) {
        drivers = driversResponse.data.drivers || driversResponse.data || [];
      } else if (driversResponse.drivers) {
        drivers = driversResponse.drivers;
      } else if (Array.isArray(driversResponse)) {
        drivers = driversResponse;
      }

      if (ambulancesResponse.success && ambulancesResponse.data) {
        ambulances = ambulancesResponse.data.ambulances || ambulancesResponse.data || [];
      } else if (ambulancesResponse.ambulances) {
        ambulances = ambulancesResponse.ambulances;
      } else if (Array.isArray(ambulancesResponse)) {
        ambulances = ambulancesResponse;
      }

      // Filter unassigned drivers and ambulances
      const unassignedDriversList = drivers.filter(driver => 
        !driver.ambulance && 
        !driver.Ambulances && 
        driver.availability_status === 'AVAILABLE'
      );
      
      const unassignedAmbulancesList = ambulances.filter(ambulance => 
        !ambulance.assigned_driver && !ambulance.assigned_driver_id && ambulance.status === 'AVAILABLE'
      );

      console.log('👥 Unassigned drivers:', unassignedDriversList.length);
      console.log('🚑 Unassigned ambulances:', unassignedAmbulancesList.length);

      setUnassignedDrivers(unassignedDriversList);
      setUnassignedAmbulances(unassignedAmbulancesList);

      // Initialize with 1 single assignment by default (clean & intuitive)
      // Users can click "+ Add Another Assignment" if they want to assign multiple pairs at once
      const maxPossible = Math.min(unassignedDriversList.length, unassignedAmbulancesList.length);
      if (maxPossible > 0) {
        setAssignments([
          {
            id: `assignment_0`,
            driver_id: '',
            ambulance_id: '',
            driver_name: '',
            ambulance_plate: ''
          }
        ]);
      } else {
        setAssignments([]);
      }

    } catch (error) {
      console.error('❌ Error loading assignment data:', error);
      onError && onError('Failed to load assignment data. Please try again.');
    } finally {
      setLoadingData(false);
    }
  };

  const handleAddAssignment = () => {
    const maxPossible = Math.min(unassignedDrivers.length, unassignedAmbulances.length);
    if (assignments.length >= maxPossible) return;

    const newId = `assignment_${Date.now()}_${assignments.length}`;
    setAssignments(prev => [
      ...prev,
      {
        id: newId,
        driver_id: '',
        ambulance_id: '',
        driver_name: '',
        ambulance_plate: ''
      }
    ]);

    setTimeout(() => {
      const el = document.getElementById(newId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  };

  const handleRemoveAssignment = (assignmentId) => {
    setAssignments(prev => {
      if (prev.length <= 1) {
        return prev.map(a => ({
          ...a,
          driver_id: '',
          ambulance_id: '',
          driver_name: '',
          ambulance_plate: ''
        }));
      }
      return prev.filter(a => a.id !== assignmentId);
    });

    setErrors(prev => {
      const next = { ...prev };
      delete next[`driver_${assignmentId}`];
      delete next[`ambulance_${assignmentId}`];
      return next;
    });
  };

  const handleDriverSelect = useCallback((assignmentId, driverId) => {
    const driver = unassignedDrivers.find(d => d.driver_id === driverId);
    setAssignments(prev => prev.map(assignment => 
      assignment.id === assignmentId 
        ? { ...assignment, driver_id: driverId, driver_name: driver?.name || '' }
        : assignment
    ));
    setErrors(prev => {
      const next = { ...prev };
      delete next[`driver_${assignmentId}`];
      delete next.general;
      return next;
    });
  }, [unassignedDrivers]);

  const handleAmbulanceSelect = useCallback((assignmentId, ambulanceId) => {
    const ambulance = unassignedAmbulances.find(a => a.vehicle_id === ambulanceId);
    setAssignments(prev => prev.map(assignment => 
      assignment.id === assignmentId 
        ? { ...assignment, ambulance_id: ambulanceId, ambulance_plate: ambulance?.plate_number || '' }
        : assignment
    ));
    setErrors(prev => {
      const next = { ...prev };
      delete next[`ambulance_${assignmentId}`];
      delete next.general;
      return next;
    });
  }, [unassignedAmbulances]);

  const getDriverOptions = useCallback((currentAssignmentId) => {
    return unassignedDrivers.map(driver => {
      const otherAssignment = assignments.find(
        a => a.driver_id === driver.driver_id && a.id !== currentAssignmentId
      );
      const otherIndex = otherAssignment 
        ? assignments.findIndex(a => a.id === otherAssignment.id) + 1 
        : null;

      return {
        id: driver.driver_id,
        primaryText: driver.name || 'Unnamed Driver',
        secondaryText: driver.license_number || '',
        extraText: driver.phone ? `📞 ${driver.phone}` : (driver.email || ''),
        disabled: Boolean(otherAssignment),
        disabledReason: otherIndex ? `Selected in #${otherIndex}` : ''
      };
    });
  }, [unassignedDrivers, assignments]);

  const getAmbulanceOptions = useCallback((currentAssignmentId) => {
    return unassignedAmbulances.map(ambulance => {
      const otherAssignment = assignments.find(
        a => a.ambulance_id === ambulance.vehicle_id && a.id !== currentAssignmentId
      );
      const otherIndex = otherAssignment 
        ? assignments.findIndex(a => a.id === otherAssignment.id) + 1 
        : null;

      return {
        id: ambulance.vehicle_id,
        primaryText: ambulance.plate_number || 'No Plate',
        secondaryText: ambulance.type?.name || 'Ambulance',
        extraText: ambulance.model ? `Model: ${ambulance.model}` : '',
        disabled: Boolean(otherAssignment),
        disabledReason: otherIndex ? `Selected in #${otherIndex}` : ''
      };
    });
  }, [unassignedAmbulances, assignments]);

  const validateAssignments = () => {
    const newErrors = {};
    const usedDrivers = new Set();
    const usedAmbulances = new Set();

    let filledCount = 0;

    assignments.forEach((assignment) => {
      const hasAnyField = Boolean(assignment.driver_id || assignment.ambulance_id);
      const isMandatory = assignments.length === 1 || hasAnyField;

      if (isMandatory) {
        if (!assignment.driver_id) {
          newErrors[`driver_${assignment.id}`] = 'Please select a driver';
        } else if (usedDrivers.has(assignment.driver_id)) {
          newErrors[`driver_${assignment.id}`] = 'Driver already selected in another assignment';
        } else {
          usedDrivers.add(assignment.driver_id);
        }

        if (!assignment.ambulance_id) {
          newErrors[`ambulance_${assignment.id}`] = 'Please select an ambulance';
        } else if (usedAmbulances.has(assignment.ambulance_id)) {
          newErrors[`ambulance_${assignment.id}`] = 'Ambulance already selected in another assignment';
        } else {
          usedAmbulances.add(assignment.ambulance_id);
        }

        if (assignment.driver_id && assignment.ambulance_id) {
          filledCount++;
        }
      }
    });

    if (filledCount === 0 && Object.keys(newErrors).length === 0) {
      newErrors.general = 'Please select both a driver and an ambulance';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 && filledCount > 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateAssignments()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      const operatorId = user?.operator?.operator_id || 
                        user?.operator_id || 
                        user?.operator?.id ||
                        user?.fleet_operator_id ||
                        user?.fleet_operator?.operator_id;

      console.log('🚀 Starting driver-ambulance assignments...');
      console.log('📊 Assignments to process:', assignments.length);

      // Filter to only process filled assignments
      const filledAssignments = assignments.filter(assignment => 
        assignment.driver_id && assignment.ambulance_id
      );

      console.log('📊 Filled assignments to process:', filledAssignments.length);

      const assignmentResults = [];
      const errors = [];

      // Process each filled assignment
      for (let i = 0; i < filledAssignments.length; i++) {
        const assignment = filledAssignments[i];
        
        try {
          console.log(`🔄 Processing assignment ${i + 1}:`, assignment);
          
          console.log(`📤 Sending assignment request:`, {
            vehicleId: assignment.ambulance_id,
            driverId: assignment.driver_id
          });
          
          const result = await apiService.assignDriverToAmbulance(assignment.ambulance_id, {
            driver_id: assignment.driver_id
          });
          
          console.log(`📥 Assignment response:`, result);

          assignmentResults.push({
            driver_name: assignment.driver_name,
            ambulance_plate: assignment.ambulance_plate,
            success: true,
            result
          });

          console.log(`✅ Assignment ${i + 1} successful:`, result);
        } catch (error) {
          console.error(`❌ Assignment ${i + 1} failed:`, error);
          errors.push({
            driver_name: assignment.driver_name,
            ambulance_plate: assignment.ambulance_plate,
            error: error.message || 'Assignment failed'
          });
        }
      }

      console.log('📊 Assignment summary:', { successful: assignmentResults.length, failed: errors.length });

      setAssignmentResult({
        successful: assignmentResults,
        failed: errors,
        total: assignments.length
      });

      if (assignmentResults.length > 0) {
        setShowSuccess(true);
        onSuccess && onSuccess({ successful: assignmentResults, failed: errors });
      } else {
        throw new Error('No assignments were successful');
      }

    } catch (error) {
      console.error('❌ Assignment error:', error);
      onError && onError(error.message || 'Failed to assign drivers to ambulances');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (inline && onCancel) {
      onCancel();
    } else {
      setIsOpen(false);
    }
    setShowSuccess(false);
    setAssignmentResult(null);
    setAssignments([]);
    setErrors({});
  };

  const AssignmentForm = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">💡 Driver-Ambulance Assignment</p>
            <p>Pair an available driver with an available ambulance. You can assign a single pair, or use &ldquo;+ Add Another Assignment&rdquo; below to assign multiple pairs at once.</p>
          </div>
        </div>
      </div>

      {assignments.length === 0 ? (
        <div className="text-center py-8">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No assignments available</h3>
          <p className="text-gray-600 mb-4">
            {unassignedDrivers.length === 0 && unassignedAmbulances.length === 0 
              ? 'No unassigned drivers or ambulances found.'
              : unassignedDrivers.length === 0 
                ? 'No unassigned drivers found.'
                : 'No unassigned ambulances found.'
            }
          </p>
          <div className="text-sm text-gray-500">
            <p>Unassigned drivers: {unassignedDrivers.length}</p>
            <p>Unassigned ambulances: {unassignedAmbulances.length}</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                Driver &amp; Vehicle Pairing
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {assignments.length === 1 
                  ? 'Assign 1 driver to an ambulance. You can add more if needed.' 
                  : `Assigning ${assignments.length} drivers to ambulances simultaneously.`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs bg-blue-50/50 text-blue-700 border-blue-200">
                {unassignedDrivers.length} unassigned drivers
              </Badge>
              <Badge variant="outline" className="text-xs bg-purple-50/50 text-purple-700 border-purple-200">
                {unassignedAmbulances.length} available ambulances
              </Badge>
            </div>
          </div>

          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center">
                <AlertCircle className="h-4 w-4 text-red-500 mr-2 flex-shrink-0" />
                <p className="text-xs text-red-800">{errors.general}</p>
              </div>
            </div>
          )}

          {assignments.map((assignment, index) => (
            <Card id={assignment.id} key={assignment.id} className="border border-gray-200 shadow-2xs overflow-visible">
              <CardHeader className="py-2.5 px-4 bg-gray-50/60 border-b border-gray-100 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center font-bold">
                    {index + 1}
                  </span>
                  {assignments.length === 1 ? 'Assignment Details' : `Assignment #${index + 1}`}
                </CardTitle>
                {assignments.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveAssignment(assignment.id)}
                    className="h-7 px-2 text-xs text-gray-500 hover:text-red-600 hover:bg-red-50 flex items-center gap-1 rounded-md transition-colors"
                    title="Remove assignment"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Remove</span>
                  </Button>
                )}
              </CardHeader>
              <CardContent className="p-4 space-y-4 overflow-visible">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Driver Selection */}
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5 text-blue-600" />
                      Select Driver *
                    </label>
                    <SearchableAssignmentSelect
                      value={assignment.driver_id}
                      onChange={(value) => handleDriverSelect(assignment.id, value)}
                      options={getDriverOptions(assignment.id)}
                      placeholder="Choose a driver"
                      searchPlaceholder="Search driver by name, license or phone..."
                      emptyMessage="No unassigned drivers found"
                      error={Boolean(errors[`driver_${assignment.id}`])}
                      icon={Users}
                    />
                    {errors[`driver_${assignment.id}`] && (
                      <p className="text-xs text-red-600">{errors[`driver_${assignment.id}`]}</p>
                    )}
                  </div>

                  {/* Ambulance Selection */}
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                      <Truck className="w-3.5 h-3.5 text-blue-600" />
                      Select Ambulance *
                    </label>
                    <SearchableAssignmentSelect
                      value={assignment.ambulance_id}
                      onChange={(value) => handleAmbulanceSelect(assignment.id, value)}
                      options={getAmbulanceOptions(assignment.id)}
                      placeholder="Choose an ambulance"
                      searchPlaceholder="Search ambulance by plate, type or model..."
                      emptyMessage="No unassigned ambulances found"
                      error={Boolean(errors[`ambulance_${assignment.id}`])}
                      icon={Truck}
                    />
                    {errors[`ambulance_${assignment.id}`] && (
                      <p className="text-xs text-red-600">{errors[`ambulance_${assignment.id}`]}</p>
                    )}
                  </div>
                </div>

                {/* Assignment Preview */}
                {assignment.driver_name && assignment.ambulance_plate && (
                  <div className="bg-emerald-50/80 border border-emerald-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-xs text-emerald-800 font-medium">
                      <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                      <span>
                        <strong>{assignment.driver_name}</strong> will be assigned to ambulance <strong>{assignment.ambulance_plate}</strong>
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}

          {/* Add Another Assignment Button */}
          {assignments.length < Math.min(unassignedDrivers.length, unassignedAmbulances.length) && (
            <Button
              type="button"
              variant="outline"
              onClick={handleAddAssignment}
              className="w-full py-2.5 border-dashed border-gray-300 hover:border-blue-500 hover:bg-blue-50/50 text-blue-600 font-medium text-xs flex items-center justify-center gap-1.5 transition-colors rounded-lg"
            >
              <Plus className="w-4 h-4" />
              <span>+ Add Another Assignment (Optional)</span>
            </Button>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t border-gray-100">
            <span className="text-xs text-gray-500">
              {assignments.filter(a => a.driver_id && a.ambulance_id).length > 0 
                ? `${assignments.filter(a => a.driver_id && a.ambulance_id).length} pair${assignments.filter(a => a.driver_id && a.ambulance_id).length > 1 ? 's' : ''} ready` 
                : 'Select both driver and ambulance'}
            </span>
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading || assignments.length === 0}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Assigning...
                  </>
                ) : (
                  <>
                    <UserCheck className="w-4 h-4 mr-2" />
                    {assignments.filter(a => a.driver_id && a.ambulance_id).length > 1
                      ? `Assign Drivers (${assignments.filter(a => a.driver_id && a.ambulance_id).length})`
                      : 'Assign Driver'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const SuccessView = () => (
    <div className="space-y-6">
      <div className="text-center">
        <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Assignments Completed!</h3>
        <p className="text-gray-600">
          Successfully assigned {assignmentResult?.successful?.length || 0} drivers to ambulances.
        </p>
      </div>

      {assignmentResult && (
        <div className="space-y-4">
          {/* Successful Assignments */}
          {assignmentResult.successful && assignmentResult.successful.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-medium text-green-800 mb-3 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Successful Assignments ({assignmentResult.successful.length})
              </h4>
              <div className="space-y-2">
                {assignmentResult.successful.map((assignment, index) => (
                  <div key={index} className="text-sm text-green-700 bg-white rounded p-2">
                    <strong>{assignment.driver_name}</strong> → <strong>{assignment.ambulance_plate}</strong>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Failed Assignments */}
          {assignmentResult.failed && assignmentResult.failed.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="font-medium text-red-800 mb-3 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Failed Assignments ({assignmentResult.failed.length})
              </h4>
              <div className="space-y-2">
                {assignmentResult.failed.map((assignment, index) => (
                  <div key={index} className="text-sm text-red-700 bg-white rounded p-2">
                    <strong>{assignment.driver_name}</strong> → <strong>{assignment.ambulance_plate}</strong>
                    <div className="text-xs text-red-600 mt-1">{assignment.error}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <Button onClick={handleClose} className="bg-green-600 hover:bg-green-700">
          Done
        </Button>
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="w-5 h-5" />
            Driver-Ambulance Assignment
          </DialogTitle>
          <DialogDescription>
            Assign available drivers to available ambulances for optimal fleet management.
          </DialogDescription>
        </DialogHeader>
        
        <div className="px-6 pb-20">
          {loadingData ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span className="text-gray-600">Loading assignment data...</span>
            </div>
          ) : showSuccess ? (
            <SuccessView />
          ) : (
            <form onSubmit={handleSubmit}>
              <AssignmentForm />
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DriverAmbulanceAssignmentModal;

